import { prefersReducedMotion } from '@theme/utilities';

/**
 * Drives `sections/scroll-story.liquid`.
 *
 * The section renders as plain stacked panels by default. This element upgrades it
 * to the pinned presentation by setting `data-enhanced`, and is the only thing that
 * does — so if this script never runs, the visitor still gets readable content
 * rather than a stack of absolutely positioned panels on top of each other.
 *
 * Which panel is active is decided by one IntersectionObserver watching a column of
 * sentinels, with a root margin that collapses the viewport to a single horizontal
 * line across its middle. Exactly one sentinel can cross that line at a time, so the
 * active index falls out of the observer directly — no scroll handler, no
 * `getBoundingClientRect` per frame, no scroll-position arithmetic to drift.
 *
 * Registered as a custom element so Shopify's section lifecycle handles teardown:
 * the Theme Editor removing a section disconnects the element, and
 * `disconnectedCallback` releases the observer.
 */
class ScrollStory extends HTMLElement {
  /** @type {IntersectionObserver | undefined} */
  #observer;

  /** @type {HTMLElement[]} */
  #panels = [];

  /** @type {HTMLElement[]} */
  #media = [];

  #active = -1;

  connectedCallback() {
    // Respect the user's motion preference by simply never enhancing: the fallback
    // layout is the accessible one, so there is nothing further to disable.
    if (prefersReducedMotion()) return;

    this.#panels = /** @type {HTMLElement[]} */ ([...this.querySelectorAll('[data-panel]')]);
    this.#media = /** @type {HTMLElement[]} */ ([...this.querySelectorAll('[data-panel-media]')]);

    const sentinels = /** @type {HTMLElement[]} */ ([...this.querySelectorAll('[data-sentinel]')]);
    // A single panel has nothing to switch between; leave it in the plain layout.
    if (sentinels.length < 2 || this.#panels.length !== sentinels.length) return;

    // Consumed by the CSS to size the scroll track (panel height × panel count).
    this.style.setProperty('--scroll-story-panels', String(sentinels.length));
    this.dataset.enhanced = '';

    this.#observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          const index = Number(/** @type {HTMLElement} */ (entry.target).dataset.sentinel);
          if (Number.isFinite(index)) this.#setActive(index);
        }
      },
      // Collapse the root to the viewport's middle line.
      { rootMargin: '-50% 0px -50% 0px', threshold: 0 }
    );

    for (const sentinel of sentinels) this.#observer.observe(sentinel);

    this.#setActive(0);
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
    this.#observer = undefined;
  }

  /**
   * @param {number} index
   */
  #setActive(index) {
    if (index === this.#active) return;
    this.#active = index;

    for (const [i, panel] of this.#panels.entries()) {
      panel.toggleAttribute('data-active', i === index);
    }

    for (const [i, item] of this.#media.entries()) {
      item.toggleAttribute('data-active', i === index);
    }
  }
}

if (!customElements.get('scroll-story')) {
  customElements.define('scroll-story', ScrollStory);
}
